import { Button, IconButton, Menu, Tooltip } from "@mui/material";
import { useFlightTranslation } from "utils/customHooks/useFlightTranslation";
import { useState } from "react";
import {
  darkModeModalBackgroundColor,
  darkModeTextColor,
} from "constants/colors";
import {
  ArrowDownward,
  ArrowUpward,
  Category,
  KeyboardArrowDown,
} from "@mui/icons-material";
import { useIsDarkMode } from "store/atoms";
import {
  GROUPABLE_METRICS,
  GroupableMetric,
  INITIAL_PROCESS_TILES_FILTER_STATE,
  ProcessTilesFilterState,
  useProcessTilesFilterState,
} from "./useProcessTilesFilterState";
import { isEqual } from "lodash";
import { useSelector } from "store/useSelector";
import { StyledMenuItemFilterControls } from "./ProcessTilesFilterControlsStyles";
import {
  useGetPageFromPath,
  useSummaryGroup,
} from "utils/customHooks/genericHooks";
import { isVariationOfAll } from "components/FlightTrends/TrendsFilters/filtersDrawerUtils";
import { useBreakpoints } from "constants/constants";

export function GroupByDropdown({ startTransition }) {
  const [filterState, setFilterState] = useProcessTilesFilterState();
  const { translate } = useFlightTranslation();
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = event => {
    setAnchorEl(p => (p ? null : event.currentTarget));
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const [isDarkMode] = useIsDarkMode();
  const active = !isEqual(
    filterState.groupBy,
    INITIAL_PROCESS_TILES_FILTER_STATE.groupBy
  );
  const dataDumpsFullRangeByProcess = useSelector(
    state => state.legacy.dataDumpsFullRangeByProcess
  );
  const hasDataDumps = Object.keys(dataDumpsFullRangeByProcess).length > 0;

  const summaryGroup = useSummaryGroup();
  const { isAllWorkSchedulesPage } = useGetPageFromPath();
  const { isTabletOrLarger } = useBreakpoints();
  let groupByLabel = `${translate(`Group${active ? " by" : ""}`)} ${filterState.groupBy?.label ?? ""}`;
  if (!isTabletOrLarger && filterState.groupBy?.label) {
    groupByLabel = filterState.groupBy?.label;
  }
  return (
    <>
      <Button
        aria-controls="process-tiles-group-controls"
        aria-haspopup="true"
        className={`btnProcessTilesDropdown${active ? " active" : ""}`}
        onClick={handleClick}
        endIcon={<KeyboardArrowDown />}
        startIcon={<Category />}
      >
        {groupByLabel}
      </Button>
      <Menu
        id="process-tiles-group-controls"
        anchorEl={anchorEl}
        keepMounted={false}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        disableScrollLock={true}
        {...(isDarkMode
          ? {
              PaperProps: {
                style: {
                  background: darkModeModalBackgroundColor,
                  color: darkModeTextColor,
                },
              },
            }
          : {})}
        variant={"menu"} // Use "menu" to prevent selected items from impacting the initial focus and the vertical alignment relative to the anchor element.
      >
        {GROUPABLE_METRICS.filter(
          groupableMetric => groupableMetric.source === "askr" || hasDataDumps
        ).map(groupableMetric => {
          const sortOrder =
            filterState.groupBy?.label === groupableMetric.label
              ? filterState.groupBy?.order
              : null;

          const isGroupByGroup = groupableMetric.customKey === "group";
          // can only group-by-group on all-work-schedules page or if the summary group is an "all" group
          const isGroupableByGroup =
            isAllWorkSchedulesPage || isVariationOfAll(summaryGroup);
          const disabled = isGroupByGroup && !isGroupableByGroup;
          const disabledText = disabled
            ? "Grouping by group is only available on multi-group pages"
            : "";
          return [
            <Tooltip title={disabledText} key={groupableMetric.label}>
              <StyledMenuItemFilterControls
                value={groupableMetric.label}
                style={disabled ? { opacity: 0.5, cursor: "default" } : {}}
                onClick={() => {
                  if (disabled) {
                    return;
                  }
                  startTransition(() => {
                    setFilterState(prev => {
                      // if we're grouping by pnlCategory and if we're already sorted by pnlCategory, unsort first
                      const p = unsortFirst(prev, groupableMetric);

                      const groupBy = { ...groupableMetric };
                      // if unsorted or not sorted by this label, sort descending
                      if (
                        !p.groupBy ||
                        p.groupBy?.label !== groupableMetric.label
                      ) {
                        return {
                          ...p,
                          groupBy: {
                            ...groupBy,
                            order: "desc",
                          },
                        };
                        // if already descending, sort ascending
                      } else if (
                        p.groupBy?.label === groupableMetric.label &&
                        p.groupBy?.order === "desc"
                      ) {
                        return {
                          ...p,
                          groupBy: { ...groupBy, order: "asc" },
                        };
                        // if already ascending, unsort
                      } else {
                        return {
                          ...p,
                          groupBy: INITIAL_PROCESS_TILES_FILTER_STATE.groupBy,
                        };
                      }
                    });
                  });
                }}
                className={
                  filterState.groupBy?.customKey &&
                  filterState.groupBy.customKey === groupableMetric.customKey
                    ? "selected"
                    : ""
                }
              >
                <div className="label">{groupableMetric.label}</div>{" "}
                <IconButton
                  className={`sortArrowButton${
                    sortOrder === "asc" ? " active" : ""
                  }`}
                  style={{ marginLeft: 64, marginRight: -8 }}
                  onClick={e => {
                    e.stopPropagation();

                    if (sortOrder === "asc") {
                      startTransition(() => {
                        setFilterState(prev => {
                          // if we're grouping by pnlCategory and if we're already sorted by pnlCategory, unsort first
                          const p = unsortFirst(prev, groupableMetric);
                          return {
                            ...p,
                            groupBy: INITIAL_PROCESS_TILES_FILTER_STATE.groupBy,
                          };
                        });
                      });
                    } else {
                      startTransition(() => {
                        setFilterState(prev => {
                          // if we're grouping by pnlCategory and if we're already sorted by pnlCategory, unsort first
                          const p = unsortFirst(prev, groupableMetric);
                          return {
                            ...p,
                            groupBy: { ...groupableMetric, order: "asc" },
                          };
                        });
                      });
                    }
                  }}
                >
                  <ArrowUpward />
                </IconButton>
                <IconButton
                  className={`sortArrowButton${
                    sortOrder === "desc" ? " active" : ""
                  }`}
                  onClick={e => {
                    e.stopPropagation();
                    if (sortOrder === "desc") {
                      startTransition(() => {
                        setFilterState(prev => {
                          // if we're grouping by pnlCategory and if we're already sorted by pnlCategory, unsort first
                          const p = unsortFirst(prev, groupableMetric);
                          return {
                            ...p,
                            groupBy: INITIAL_PROCESS_TILES_FILTER_STATE.groupBy,
                          };
                        });
                      });
                    } else {
                      startTransition(() =>
                        setFilterState(prev => {
                          // if we're grouping by pnlCategory and if we're already sorted by pnlCategory, unsort first
                          const p = unsortFirst(prev, groupableMetric);
                          return {
                            ...p,
                            groupBy: {
                              ...groupableMetric,
                              order: "desc",
                            },
                          };
                        })
                      );
                    }
                  }}
                >
                  <ArrowDownward />
                </IconButton>
              </StyledMenuItemFilterControls>
            </Tooltip>,
          ];
        })}
      </Menu>
    </>
  );
}

function unsortFirst(
  prev: ProcessTilesFilterState,
  groupableMetric: GroupableMetric
) {
  return groupableMetric.customKey === "pnlCategory" &&
    prev.sortBy?.customKey === "pnlCategory"
    ? {
        ...prev,
        sortBy: INITIAL_PROCESS_TILES_FILTER_STATE.sortBy,
      }
    : prev;
}
